import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, ClipboardCheck, Loader2, ImagePlus, Pencil, Wallet } from "lucide-react";
import { DocImage } from "@/components/DocImage";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

export const RESULTS = { layak: "Layak", perlu_pertimbangan: "Perlu Pertimbangan", tidak_layak: "Tidak Layak" };
export const METHOD_LABEL = { tunai: "Tunai di Kantor CU", sukarela: "Simpanan Sukarela", bank: "Transfer Bank" };
export const fmtWhen = (s) => new Date(s).toLocaleString("id-ID", { dateStyle: "full", timeStyle: "short", timeZone: "Asia/Jakarta" }) + " WIB";
const inp = "h-9 rounded-lg border border-slate-300 px-2 text-sm bg-white";
const toLocal = (s) => { const d = new Date(s); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 16); };

const useLoanPerm = (user) => {
  const [ok, setOk] = useState(user.role === "superadmin");
  useEffect(() => {
    if (user.role === "superadmin") return;
    api.get("/admin/permissions").then((r) => setOk((r.data.permissions.loans || []).includes(user.position))).catch(() => {});
  }, [user.role, user.position]);
  return ok;
};

const ScheduleForm = ({ loan, onDone }) => {
  const { user } = useAuth();
  const cur = loan.survey?.schedule;
  const [f, setF] = useState({ at: cur ? toLocal(cur.scheduled_at) : "", officer: cur?.officer || user.name, note: cur?.note || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.at) return toast.error("Pilih tanggal & jam survei");
    setBusy(true);
    try {
      await api.post(`/admin/loans/${loan.loan_id}/survey-schedule`, { scheduled_at: new Date(f.at).toISOString(), officer: f.officer, note: f.note || null });
      toast.success("Jadwal survei dikirim ke anggota");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap gap-2 items-center mt-1.5">
      <input type="datetime-local" value={f.at} onChange={(e) => setF({ ...f, at: e.target.value })} data-testid={`survey-date-${loan.loan_id}`} className={inp} />
      <input value={f.officer} onChange={(e) => setF({ ...f, officer: e.target.value })} placeholder="Nama petugas" maxLength={80} data-testid={`survey-officer-${loan.loan_id}`} className={`${inp} w-40`} />
      <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Catatan (opsional)" maxLength={300} data-testid={`survey-note-${loan.loan_id}`} className={`${inp} w-48`} />
      <button onClick={save} disabled={busy} data-testid={`survey-schedule-save-${loan.loan_id}`} className="h-9 px-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}{cur ? "Ubah Jadwal" : "Kirim Jadwal"}
      </button>
    </div>
  );
};

const ReportForm = ({ loan, onDone }) => {
  const rep = loan.survey?.report;
  const [note, setNote] = useState(rep?.note || "");
  const [result, setResult] = useState(rep?.result || "layak");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (note.trim().length < 5) return toast.error("Tulis catatan hasil survei");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("note", note.trim());
      fd.append("result", result);
      files.forEach((x) => fd.append("files", x, x.name));
      await api.post(`/admin/loans/${loan.loan_id}/survey-report`, fd);
      toast.success("Laporan survei tersimpan");
      setFiles([]);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-1.5 space-y-2">
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} placeholder="Catatan hasil survei: kondisi rumah/usaha, kesesuaian data, dll." data-testid={`survey-report-note-${loan.loan_id}`}
        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
      <div className="flex flex-wrap gap-2 items-center">
        <select value={result} onChange={(e) => setResult(e.target.value)} data-testid={`survey-report-result-${loan.loan_id}`} className={inp}>
          {Object.entries(RESULTS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label className="h-9 px-3 rounded-lg border border-slate-300 bg-white text-xs font-semibold inline-flex items-center gap-1.5 cursor-pointer">
          <ImagePlus className="h-4 w-4" />{files.length ? `${files.length} foto dipilih` : "Tambah foto survei"}
          <input type="file" accept="image/*" multiple className="hidden" data-testid={`survey-report-files-${loan.loan_id}`} onChange={(e) => setFiles([...e.target.files].slice(0, 8))} />
        </label>
        <button onClick={save} disabled={busy} data-testid={`survey-report-save-${loan.loan_id}`} className="h-9 px-3 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />}{rep ? "Perbarui Laporan" : "Simpan Laporan"}
        </button>
      </div>
    </div>
  );
};

export const SurveyPanel = ({ loan, onDone }) => {
  const { user } = useAuth();
  const canAct = useLoanPerm(user);
  const s = loan.survey || {};
  const photos = (loan.documents || []).filter((d) => d.doc_type === "survei");
  const active = loan.status === "pending" && loan.stage === "survei" && canAct;
  if (loan.stage !== "survei" && !s.schedule && !s.report) return null;
  return (
    <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-xs" data-testid={`survey-panel-${loan.loan_id}`}>
      <div className="font-bold text-amber-900 flex items-center gap-1.5"><CalendarClock className="h-4 w-4" />Survei Lapangan</div>
      {s.schedule ? <div className="text-slate-700 mt-0.5" data-testid={`survey-schedule-info-${loan.loan_id}`}>Jadwal: <b>{fmtWhen(s.schedule.scheduled_at)}</b> • Petugas {s.schedule.officer}{s.schedule.note ? ` • ${s.schedule.note}` : ""}</div>
        : <div className="text-slate-600 mt-0.5">Belum dijadwalkan.</div>}
      {s.reschedule?.status === "pending" && (
        <div className="mt-1 rounded-lg bg-white border border-red-200 px-2 py-1 text-red-700" data-testid={`survey-reschedule-${loan.loan_id}`}>
          Anggota minta jadwal ulang: “{s.reschedule.reason}”{s.reschedule.preferred_at ? ` • usul ${fmtWhen(s.reschedule.preferred_at)}` : ""}
        </div>
      )}
      {active && <ScheduleForm loan={loan} onDone={onDone} />}
      {s.report && (
        <div className="mt-2 text-slate-800" data-testid={`survey-report-info-${loan.loan_id}`}>
          Hasil: <b>{RESULTS[s.report.result]}</b> • oleh {s.report.by}<div className="whitespace-pre-line">{s.report.note}</div>
        </div>
      )}
      {photos.length > 0 && <div className="flex flex-wrap gap-1.5 mt-1.5">{photos.map((p) => <DocImage key={p.doc_id} docId={p.doc_id} alt="Foto survei" className="h-16 w-20 object-cover rounded-md border border-slate-200" />)}</div>}
      {active && s.schedule && <ReportForm loan={loan} onDone={onDone} />}
    </div>
  );
};

export const DisburseMethod = ({ loan, onDone }) => {
  const { user } = useAuth();
  const canEdit = (user.role === "superadmin" || user.position === "Manager") && ["pending", "disetujui"].includes(loan.status);
  const [edit, setEdit] = useState(false);
  const [f, setF] = useState({ disburse_method: loan.disburse_method || "tunai", bank_name: loan.bank_name || "", bank_account: loan.bank_account || "", bank_holder: loan.bank_holder || "" });
  if (!loan.disburse_method && !canEdit) return null;
  const save = async () => {
    try {
      await api.put(`/admin/loans/${loan.loan_id}/disburse-method`, f);
      toast.success("Metode pencairan dikoreksi");
      setEdit(false);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="text-xs text-slate-700 mt-1" data-testid={`admin-loan-method-${loan.loan_id}`}>
      <span className="inline-flex items-center gap-1"><Wallet className="h-3.5 w-3.5" />Pencairan: <b>{METHOD_LABEL[loan.disburse_method] || "-"}</b>
        {loan.disburse_method === "bank" && ` • ${loan.bank_name} ${loan.bank_account} a.n. ${loan.bank_holder}`}</span>
      {loan.method_edited_by && <span className="text-slate-500"> (dikoreksi {loan.method_edited_by})</span>}
      {canEdit && !edit && <button onClick={() => setEdit(true)} data-testid={`admin-loan-method-edit-${loan.loan_id}`} className="ml-2 text-blue-700 font-semibold inline-flex items-center gap-0.5"><Pencil className="h-3 w-3" />Koreksi</button>}
      {edit && (
        <div className="flex flex-wrap gap-2 mt-1.5">
          <select value={f.disburse_method} onChange={(e) => setF({ ...f, disburse_method: e.target.value })} data-testid={`admin-loan-method-select-${loan.loan_id}`} className={inp}>
            {Object.entries(METHOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          {f.disburse_method === "bank" && ["bank_name:Nama bank", "bank_account:No. rekening", "bank_holder:Atas nama"].map((x) => {
            const [k, ph] = x.split(":");
            return <input key={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} placeholder={ph} data-testid={`admin-loan-method-${k}-${loan.loan_id}`} className={`${inp} w-36`} />;
          })}
          <button onClick={save} data-testid={`admin-loan-method-save-${loan.loan_id}`} className="h-9 px-3 rounded-lg bg-blue-700 text-white font-bold">Simpan</button>
          <button onClick={() => setEdit(false)} className="h-9 px-3 rounded-lg border border-slate-300 font-semibold">Batal</button>
        </div>
      )}
    </div>
  );
};
